import React from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Responsive } from '../../components/common/Responsive';
import { SearchCourses } from '../../components/common/SearchCourses';
import { FilterCourses, FilterCoursesProps } from '../../components/filter-courses/FilterCourses';
import { FilterCoursesModal } from '../../components/filter-courses/FilterCoursesModal';
import { InitialParams, useSearch } from '../../hooks/search/useSearch';
import { NoResults } from '../../components/search/NoResults';
import { SearchResults } from '../../components/search/SearchResults';
import { SearchSortBy } from '../../components/search/SearchSortBy';
import { ProductCategory } from '../../models/globals';

interface SearchProps {
    autocompleteApiPath: string;
    initialParams: InitialParams;
    productCategories: ProductCategory[];
    resultsApiPath: string;
    searchPath: string;
}

const Search: React.FC<SearchProps> = ({
    autocompleteApiPath,
    initialParams,
    productCategories,
    resultsApiPath,
    searchPath,
}) => {
    const {
        courseCountMap,
        courseTypes,
        dispatch,
        visibleProductTiles,
        handleFilterChange,
        handleSearchSubmit,
        isLoading,
        productTiles,
        searchTerms,
        subTopics,
        topics,
        sortInUse,
    } = useSearch({ initialParams, productCategories, resultsApiPath });
    const commonFilterProps: FilterCoursesProps = {
        courseCountMap,
        courseTypes,
        handleFilterChange,
        productCategories,
        subTopics,
        topics,
    };

    return (
        <Container className="container-classic">
            <Row className="m-0">
                <Responsive className="bg-light col-3 py-3" testIdSuffix="filter-courses" viewMode="desktop-and-tablet">
                    <FilterCourses {...commonFilterProps} />
                </Responsive>
                <Col className="px-0 px-md-5">
                    <h1 className="font-weight-light pt-4 search-header text-black">Search Results</h1>
                    <SearchCourses
                        autocompleteApiPath={autocompleteApiPath}
                        className="pt-4"
                        id="search-courses"
                        inputValue={searchTerms}
                        isLoadingResults={isLoading}
                        onSubmit={handleSearchSubmit}
                        placeholder="Search"
                        searchPath={searchPath}
                    />
                    {!!productTiles.length && (
                        <p className="font-weight-light my-2 pt-1 text-secondary">
                            {productTiles.length === 1 ? '1 Result' : `${productTiles.length} Results`}
                        </p>
                    )}
                    <Row className="align-items-center pt-4">
                        {!!visibleProductTiles.length && (
                            <Col xs={12} md="auto">
                                <h2 className="font-weight-light m-0 search-header text-secondary">Courses</h2>
                            </Col>
                        )}
                        {/* Filter settings should be reachable even if results are empty - they could be the reason */}
                        {/* The open button should be hidden while loading, but the modal should remain if already open */}
                        <Responsive className="col my-3 text-left" testIdSuffix="filter-courses" viewMode="mobile">
                            <FilterCoursesModal {...commonFilterProps} isLoading={isLoading} />
                        </Responsive>
                        {!!visibleProductTiles.length && <SearchSortBy dispatch={dispatch} sortInUse={sortInUse} />}
                    </Row>
                    {visibleProductTiles.length || isLoading ? (
                        <SearchResults
                            dispatch={dispatch}
                            productTiles={productTiles}
                            visibleProductTiles={visibleProductTiles}
                        />
                    ) : (
                        <NoResults searchTerms={searchTerms} />
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export { Search, SearchProps };
